import { isNotUndefined } from '@ms/yammer-data-support';
import type { AutocompleteUserResultFilterType } from '@ms/yammer-graphql';
import createCachedSelector from 're-reselect';

import { isReplyPublisher } from '../../../state/publishers/types';
import type { Publisher, User, YammerState } from '../../../state/types';
import { getAllPossibleParticipantIds } from '../../publishers/existingThreadParticipantSelectors';
import { findByIdThrows as findPublisherByIdThrows } from '../../publishers/findPublisherSelectors';
import { getFulfilledUsers } from '../../users/selectors';
import createDeepEqualSelector from '../createDeepEqualSelector';

import type { GetTypeAheadResultsOptions, TypeaheadResults } from './selectors';
import {
  extractSearchTerm,
  getCacheKey,
  getRegularTypeaheadResults,
  getRegularTypeaheadStatuses,
  getTypeaheadResults,
  getTypeaheadStatuses,
  maxUserResultsCount,
} from './selectors';

const matchUserOnName = (user: User, search: string) => {
  const normalizedName = user.name.toLowerCase().replace(/.\(\)/g, '').replace('-', ' ');
  const normalizedTerm = search.replace(/[.*+"@&:;,?^~!$%{}()|[\]\\]/g, '\\$&');
  const termAtStartOfWordRegexp = new RegExp(`(?:^|\\W)${normalizedTerm}`, 'gi');

  return normalizedName.match(termAtStartOfWordRegexp) != null;
};

const removeDuplicateUserItems = (users: User[]) => {
  const withoutDuplicates: User[] = [];
  const addedUserIds: User['id'][] = [];

  for (const user of users) {
    if (!addedUserIds.includes(user.id)) {
      addedUserIds.push(user.id);
      withoutDuplicates.push(user);
    }
  }

  return withoutDuplicates;
};

type GetTypeaheadResultsIncorporatingRecipients = (
  state: YammerState,
  publisherId: Publisher['id'],
  selectorOptions: GetTypeAheadResultsOptions
) => TypeaheadResults;
export const getTypeaheadResultsIncorporatingRecipients: GetTypeaheadResultsIncorporatingRecipients =
  createCachedSelector(
    extractSearchTerm,
    getRegularTypeaheadResults,
    getRegularTypeaheadStatuses,
    getAllPossibleParticipantIds,
    getFulfilledUsers,
    findPublisherByIdThrows,
    (search, results, statuses, candidateIds, fulfilledUsers, publisher) => {
      if (!isReplyPublisher(publisher)) {
        return results;
      }

      if (statuses.users !== 'fulfilled') {
        return results;
      }

      const nonNullCandidateIds = candidateIds.filter(isNotUndefined);
      const candidates = nonNullCandidateIds.map((userId) => fulfilledUsers[userId]);
      const filteredCandidates = candidates.filter((user) => matchUserOnName(user, search));
      const updatedUsers = [...filteredCandidates, ...results.users];
      const updatedUsersWithoutDuplicates = removeDuplicateUserItems(updatedUsers);

      return {
        ...results,
        users: updatedUsersWithoutDuplicates,
      };
    }
  )(
    (
      _state,
      publisherId,
      {
        search,
        includeUsers,
        includeGroups,
        includeTopics,
        includeCampaigns,
        includeOfficeGroups,
        groupIds,
        userResultFilters,
        groupResultFilter,
        topicResultFilter,
        officeGroupResultFilter,
      }
    ) =>
      getCacheKey({
        search,
        includeUsers,
        includeGroups,
        includeTopics,
        includeOfficeGroups,
        includeCampaigns,
        groupIds,
        userResultFilters,
        groupResultFilter,
        topicResultFilter,
        officeGroupResultFilter,
        publisherId,
      }),
    {
      selectorCreator: createDeepEqualSelector,
    }
  );

type GetTypeaheadUsers = (
  state: YammerState,
  search: string,
  options?: { readonly userResultFilters?: AutocompleteUserResultFilterType[] }
) => User[];
export const getTypeaheadUsers: GetTypeaheadUsers = createCachedSelector(
  (state: YammerState, search: string, options?: { readonly userResultFilters?: AutocompleteUserResultFilterType[] }) =>
    getTypeaheadResults(state, { search, includeUsers: true, userResultFilters: options?.userResultFilters }),
  (typeaheadResults) => typeaheadResults.users.slice(0, maxUserResultsCount)
)((_state: YammerState, search: string) => search);

type GetTypeaheadUsersIncorporatingRecipients = (
  state: YammerState,
  publisherId: Publisher['id'],
  search: string,
  options?: { readonly userResultFilters?: AutocompleteUserResultFilterType[] }
) => User[];
export const getTypeaheadUsersIncorporatingRecipients: GetTypeaheadUsersIncorporatingRecipients = createCachedSelector(
  (state: YammerState, publisherId: Publisher['id'], search: string, options = { userResultFilters: undefined }) =>
    getTypeaheadResultsIncorporatingRecipients(state, publisherId, {
      search,
      includeUsers: true,
      userResultFilters: options?.userResultFilters,
    }),
  (typeaheadResults) => typeaheadResults.users.slice(0, maxUserResultsCount)
)((_state: YammerState, search: string) => search);

type GetIsLoadingUsers = (
  state: YammerState,
  search: string,
  userResultFilters?: AutocompleteUserResultFilterType[]
) => boolean;
export const getIsLoadingUsers: GetIsLoadingUsers = (state, search, userResultFilters) =>
  getTypeaheadStatuses(state, { search, userResultFilters }).users === 'pending';
